/**
 * @fileoverview The activity behind an accepted AI-drafted entry, shown in
 * the entry detail sheet to its practitioner only (MOR-2332 AC-3, MOR-2335
 * AC-4). Approvers see the entry's narrative and time, never the raw
 * mailbox, calendar or file activity.
 *
 * Dependencies:
 * - Convex: timesheets.drafting.entryEvidence
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives";
import { DraftEvidenceList } from "./DraftEvidenceList";
import { BASIS_LABEL, formatMinutes } from "./draftSuggestions.helpers";

export function EntryEvidenceSection({
  entryId,
}: {
  entryId: Id<"timeEntries">;
}) {
  const { data } = useQuery(
    convexQuery(api.timesheets.drafting.entryEvidence, { entryId }),
  );
  if (!data || !data.visible || data.evidence.length === 0) return null;
  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        Evidence
        {data.durationBasis && (
          <Badge color="gray" variant="soft" size="sm">
            {BASIS_LABEL[data.durationBasis]}
            {data.scheduledMinutes !== undefined &&
              ` · booked ${formatMinutes(data.scheduledMinutes)}`}
          </Badge>
        )}
      </h4>
      <p className="text-xs text-muted-foreground">
        Drafted from your activity. Only you can see this list.
      </p>
      <DraftEvidenceList evidence={data.evidence} />
    </div>
  );
}
